import {
    Button,
    Icon,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from "@elit-hair/elit-ui";
import { useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { IoEarthOutline } from "react-icons/io5";
import { TbChevronDown, TbRefresh } from "react-icons/tb";

import { cn } from "@/lib/utils";
import { useClinic } from "@/providers/hooks/use-clinic";
import { useAssignedClinics } from "@/queries/clinic.queries";
import { getCountryCode } from "@/utils/country-code";

type ClinicSelectorProps = {
    /** Whether the sidebar is collapsed */
    isCollapsed?: boolean;
};

export const ClinicSelector = ({ isCollapsed = false }: ClinicSelectorProps) => {
    const [isClinicPopoverOpen, setIsClinicPopoverOpen] = useState(false);
    const { t } = useTranslation();
    const { selectedClinic, setSelectedClinic } = useClinic();
    const { data: availableClinics = [], isLoading, error, refetch } = useAssignedClinics();

    // Don't show selector if user has no clinics assigned (after loading completes)
    if (!isLoading && availableClinics.length === 0) {
        return null;
    }

    const handleSelectClinic = (clinicId: string | undefined) => {
        if (clinicId) {
            setSelectedClinic(clinicId);
        }
        setIsClinicPopoverOpen(false);
    };

    // Get the active clinic
    const getActiveClinic = () => {
        if (!selectedClinic) {
            return null;
        }

        const clinic = availableClinics.find((c) => c.id === selectedClinic);
        if (!clinic) {
            return null;
        }

        const countryCode = clinic.address?.country ? getCountryCode(clinic.address.country) : null;
        const locationText =
            clinic.address?.city && clinic.address?.country
                ? `${clinic.address.city}, ${clinic.address.country}`
                : clinic.address?.country || clinic.address?.city || "";

        return {
            id: clinic.id,
            name: clinic.name || clinic.id,
            locationText,
            countryCode,
        };
    };

    const activeClinic = getActiveClinic();

    // Get display text for selected clinic
    const getDisplayText = () => {
        if (!activeClinic) {
            return t("select_clinic");
        }
        return activeClinic.name;
    };

    // Get display subtext for selected clinic
    const getDisplaySubtext = () => {
        if (!activeClinic) {
            return "";
        }
        return activeClinic.locationText;
    };

    // Filter and sort clinics
    const sortedClinics =
        availableClinics
            .filter((clinic) => clinic.name || clinic.id)
            .sort((a, b) => (a.name?.localeCompare(b.name ?? "") ?? 0) || 0) || [];

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <Popover open={isClinicPopoverOpen} onOpenChange={setIsClinicPopoverOpen}>
                    <PopoverTrigger asChild>
                        <SidebarMenuButton
                            className={cn(
                                "text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer py-5"
                            )}
                            tooltip={isCollapsed ? t("select_clinic") : undefined}
                        >
                            {activeClinic ? (
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        {activeClinic.countryCode ? (
                                            <CircleFlag
                                                countryCode={activeClinic.countryCode}
                                                className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] shrink-0"
                                            />
                                        ) : (
                                            <div className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] bg-gray-200 flex items-center justify-center shrink-0">
                                                <Icon icon={IoEarthOutline} size={16} />
                                            </div>
                                        )}
                                        <div className="flex flex-col items-start min-w-0 flex-1">
                                            <span className="font-medium truncate w-full">{getDisplayText()}</span>
                                            <span className="text-xs text-muted-foreground truncate w-full">
                                                {getDisplaySubtext()}
                                            </span>
                                        </div>
                                    </div>
                                    <Icon icon={TbChevronDown} size={20} className="shrink-0" />
                                </div>
                            ) : (
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2">
                                        <div className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] bg-gray-200 flex items-center justify-center shrink-0">
                                            <Icon icon={IoEarthOutline} size={16} />
                                        </div>
                                        <span>{t("select_clinic")}</span>
                                    </div>
                                    <Icon icon={TbChevronDown} size={20} className="shrink-0" />
                                </div>
                            )}
                        </SidebarMenuButton>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2 overflow-hidden" align="start" sideOffset={5}>
                        <div className="flex flex-col">
                            <p className="text-sm text-muted-foreground opacity-50 p-2">{t("select_clinic")}</p>
                            <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
                                {isLoading ? (
                                    <div className="flex justify-center items-center p-4"></div>
                                ) : error ? (
                                    <div className="text-destructive p-4 text-sm">{error.message}</div>
                                ) : (
                                    <>
                                        {sortedClinics.length > 0 ? (
                                            sortedClinics.map((clinic) => {
                                                const countryCode = clinic.address?.country
                                                    ? getCountryCode(clinic.address.country)
                                                    : null;

                                                const locationText =
                                                    clinic.address?.city && clinic.address?.country
                                                        ? `${clinic.address.city}, ${clinic.address.country}`
                                                        : clinic.address?.country || clinic.address?.city || "";

                                                const isSelected = selectedClinic === clinic.id;

                                                return (
                                                    <Button
                                                        key={clinic.id}
                                                        variant="ghost"
                                                        className={cn(
                                                            "w-full justify-start gap-2 py-6 px-4 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer",
                                                            isSelected && "bg-muted text-foreground"
                                                        )}
                                                        onClick={() => handleSelectClinic(clinic.id)}
                                                    >
                                                        {countryCode ? (
                                                            <CircleFlag
                                                                countryCode={countryCode}
                                                                className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] bg-gray-200 flex items-center justify-center shrink-0">
                                                                <Icon icon={IoEarthOutline} size={16} />
                                                            </div>
                                                        )}
                                                        <div className="flex flex-col items-start min-w-0 flex-1">
                                                            <span className="font-medium text-left truncate w-full">
                                                                {clinic.name || clinic.id}
                                                            </span>
                                                            {locationText && (
                                                                <span className="text-xs text-muted-foreground text-left truncate w-full">
                                                                    {locationText}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </Button>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center text-muted-foreground py-4 text-sm">
                                                {t("no_available_clinic_found")}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="flex justify-end pt-2 border-t mt-2">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => refetch()}
                                    disabled={isLoading}
                                    className="h-8"
                                >
                                    <Icon icon={TbRefresh} size={14} />
                                    <span className="ml-1 text-xs">{t("refresh")}</span>
                                </Button>
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            </SidebarMenuItem>
        </SidebarMenu>
    );
};
